/**
 * Pure rules for the SQL server storage configuration (the Azure "Configure storage" blade): the recommended default,
 * what a group of disks adds up to, the performance notices, and validation. No React, so it is all unit-tested directly.
 */
import type { SqlDiskGroup, SqlDiskSku, SqlDiskType, SqlStorageConfig, SqlStorageRecommendation } from '@/types/infrastructure'

export const DEFAULT_DATA_LOCATION = 'F:\\data'
export const DEFAULT_LOG_LOCATION = 'G:\\log'
export const DEFAULT_TEMPDB_LOCATION = 'H:\\tempDb'
/** The size Azure recommends to start with for data and log. */
export const DEFAULT_DISK_SIZE_GB = 1024

export type StorageSection = 'data' | 'log' | 'tempDb'

// ─── Disk sizes ──────────────────────────────────────────────────────────────

/** The sizes of one disk type, smallest first. */
export function skusOfType(skus: readonly SqlDiskSku[], diskType: SqlDiskType): SqlDiskSku[] {
  return skus.filter((sku) => sku.diskType === diskType).sort((a, b) => a.sizeGb - b.sizeGb)
}

/**
 * The size of `diskType` closest to `sizeGb` (the larger one on a tie), so that changing the type keeps roughly the same
 * capacity. Falls back to the closest size of any type when the type has none. Null when there are no sizes at all.
 */
export function pickSku(skus: readonly SqlDiskSku[], diskType: SqlDiskType, sizeGb: number): SqlDiskSku | null {
  const candidates = skusOfType(skus, diskType)
  const pool = candidates.length > 0 ? candidates : [...skus]
  let best: SqlDiskSku | null = null
  for (const sku of pool) {
    const distance = Math.abs(sku.sizeGb - sizeGb)
    const bestDistance = best ? Math.abs(best.sizeGb - sizeGb) : Infinity
    if (distance < bestDistance || (distance === bestDistance && best && sku.sizeGb > best.sizeGb)) best = sku
  }
  return best
}

/** The recommended configuration: the recommended disk type at 1024 GiB for data and log, tempdb on the local SSD. */
export function defaultSqlStorage(recommendation: SqlStorageRecommendation): SqlStorageConfig | null {
  const sku = pickSku(recommendation.skus, recommendation.vm.recommendedDiskType, DEFAULT_DISK_SIZE_GB)
  if (!sku) return null
  const group = (driveLocation: string): SqlDiskGroup => ({ driveLocation, sku, count: 1 })
  return {
    vm: recommendation.vm,
    data: group(DEFAULT_DATA_LOCATION),
    log: group(DEFAULT_LOG_LOCATION),
    tempDb: { ...group(DEFAULT_TEMPDB_LOCATION), useLocalSsd: true },
    systemDatabasesOnDataDrive: false,
  }
}

// ─── Totals and descriptions ─────────────────────────────────────────────────

export interface DiskTotals {
  disks: number
  sizeGb: number
  iops: number
  throughputMbps: number
}

const validCount = (count: number) => (Number.isInteger(count) && count > 0 ? count : 0)

/** What a group adds up to. An empty or invalid count counts as no disks. */
export function groupTotals(group: SqlDiskGroup): DiskTotals {
  const disks = validCount(group.count)
  return { disks, sizeGb: group.sku.sizeGb * disks, iops: group.sku.maxIops * disks, throughputMbps: group.sku.maxThroughputMbps * disks }
}

/** The groups that have disks of their own: data, log, and tempdb unless it uses the local SSD. */
function dedicatedGroups(config: SqlStorageConfig): SqlDiskGroup[] {
  return config.tempDb.useLocalSsd ? [config.data, config.log] : [config.data, config.log, config.tempDb]
}

export function storageTotals(config: SqlStorageConfig): DiskTotals {
  return dedicatedGroups(config).reduce<DiskTotals>(
    (sum, group) => {
      const totals = groupTotals(group)
      return { disks: sum.disks + totals.disks, sizeGb: sum.sizeGb + totals.sizeGb, iops: sum.iops + totals.iops, throughputMbps: sum.throughputMbps + totals.throughputMbps }
    },
    { disks: 0, sizeGb: 0, iops: 0, throughputMbps: 0 },
  )
}

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`

/** "1024 GiB, 5000 IOPS, 200 MB/s, Premium SSD": the summary line of the Storage configuration. */
export function describeGroup(group: SqlDiskGroup): string {
  const totals = groupTotals(group)
  return `${totals.sizeGb} GiB, ${totals.iops} IOPS, ${totals.throughputMbps} MB/s, ${group.sku.diskType}`
}

/** "1024 GiB, 5000 IOPS, 200 MB/s, 1 data disk": the line under the disk table. */
export function describeDisks(group: SqlDiskGroup): string {
  const totals = groupTotals(group)
  return `${totals.sizeGb} GiB, ${totals.iops} IOPS, ${totals.throughputMbps} MB/s, ${plural(totals.disks, 'data disk')}`
}

// ─── Notices ─────────────────────────────────────────────────────────────────

export interface StorageNotice {
  level: 'warning' | 'info'
  message: string
}

/**
 * Performance notices, the same ones Azure shows. They never block: asking for more than the VM can deliver, or less than it
 * could, is allowed, but the user is told.
 */
export function storageNotices(config: SqlStorageConfig): StorageNotice[] {
  const totals = storageTotals(config)
  const { vm } = config
  if (totals.disks === 0) return []
  const notices: StorageNotice[] = []
  if (totals.throughputMbps > vm.maxThroughputMbps) {
    notices.push({
      level: 'warning',
      message: `The desired performance might not be reached due to the maximum virtual machine disk performance cap. The selected VM size (${vm.vmSize}) only supports up to ${vm.maxThroughputMbps} disk max throughput in MBps (currently ${totals.throughputMbps} in MBps).`,
    })
  }
  if (totals.iops > vm.maxIops) {
    notices.push({
      level: 'warning',
      message: `The desired performance might not be reached due to the maximum virtual machine disk performance cap. The selected VM size (${vm.vmSize}) only supports up to ${vm.maxIops} IOPS (currently ${totals.iops} IOPS).`,
    })
  } else if (totals.iops < vm.maxIops) {
    notices.push({
      level: 'info',
      message: `The requested disk performance level (${totals.iops} IOPS) is lower than the maximum virtual machine disk performance cap. The selected VM size ${vm.vmSize} supports up to ${vm.maxIops} IOPS. Consider provisioning more IOPS for best performance.`,
    })
  }
  return notices
}

// ─── Validation ──────────────────────────────────────────────────────────────

/** The drive letter of a location such as `F:\data`, upper case, or '' when there is none. */
export function driveLetterOf(location: string): string {
  const match = /^([A-Za-z]):\\/.exec(location.trim())
  return match ? match[1].toUpperCase() : ''
}

/** A location is a drive letter E to Z, then one or more folders: `F:\data`, `G:\sql\logs`. */
export function driveLocationProblem(location: string): string | null {
  const value = location.trim()
  if (value === '') return 'Enter a drive location.'
  if (value.length > 260) return 'The drive location is too long.'
  if (!/^[A-Za-z]:\\/.test(value)) return 'Start with a drive letter and a backslash, for example F:\\data.'
  const letter = value[0].toUpperCase()
  if (letter < 'E' || letter > 'Z') return 'Use drive E to Z (C is the OS disk, D the temporary disk).'
  const folders = value.slice(3).split('\\')
  if (folders.length === 1 && folders[0] === '') return 'Add a folder after the drive, for example F:\\data.'
  if (folders.some((folder) => folder === '')) return 'A folder name cannot be empty, and the location cannot end with a backslash.'
  if (folders.some((folder) => /[<>:"/|?*]/.test(folder))) return 'A folder name cannot contain < > : " / | ? *'
  if (folders.some((folder) => /[. ]$/.test(folder))) return 'A folder name cannot end with a dot or a space.'
  return null
}

export function countProblem(count: number, max: number): string | null {
  if (!Number.isInteger(count) || count < 1 || count > max) return `Number of disks must be a whole number from 1 to ${max}.`
  return null
}

/** What is wrong in one section, per field, so each message can sit next to the field it is about. */
export interface SectionProblems {
  location: string | null
  count: string | null
}

export const sectionHasProblem = (problems: SectionProblems) => problems.location !== null || problems.count !== null

export interface SqlStorageProblems {
  data: SectionProblems
  log: SectionProblems
  tempDb: SectionProblems
  /** About the configuration as a whole, for example too many disks for the VM. */
  general: string[]
}

const noProblems = (): SectionProblems => ({ location: null, count: null })

/** The problems of each section, so the editor can mark the section that needs attention. */
export function sqlStorageProblems(config: SqlStorageConfig | null): SqlStorageProblems {
  const result: SqlStorageProblems = { data: noProblems(), log: noProblems(), tempDb: noProblems(), general: [] }
  if (!config) {
    result.general.push('The storage recommendation has not loaded yet.')
    return result
  }
  const max = config.vm.maxDataDisks
  const sections: [StorageSection, SqlDiskGroup][] = [['data', config.data], ['log', config.log]]
  if (!config.tempDb.useLocalSsd) sections.push(['tempDb', config.tempDb])

  const letters = new Map<string, string>()
  const names: Record<StorageSection, string> = { data: 'data storage', log: 'log storage', tempDb: 'TempDB storage' }
  for (const [section, group] of sections) {
    const location = driveLocationProblem(group.driveLocation)
    if (location) result[section].location = location
    else {
      const letter = driveLetterOf(group.driveLocation)
      const owner = letters.get(letter)
      if (owner) result[section].location = `Drive ${letter}: is already used by ${owner}. Use a different drive letter.`
      else letters.set(letter, names[section])
    }
    result[section].count = countProblem(group.count, max)
  }

  const totals = storageTotals(config)
  if (totals.disks > max) {
    result.general.push(`The VM size ${config.vm.vmSize} supports at most ${max} data disks, but this configuration uses ${totals.disks}.`)
  }
  return result
}

/** The headings of the sections, as the Azure blade has them. */
export const SECTION_TITLES: Record<StorageSection, string> = { data: 'Data storage', log: 'Log storage', tempDb: 'TempDB storage' }

const MESSAGE_PREFIX: Record<StorageSection, string> = { data: 'SQL server data storage', log: 'SQL server log storage', tempDb: 'SQL server tempdb storage' }

/** Every blocking problem, each saying where it is, in the style of the other servers' messages. Empty when valid. */
export function validateSqlStorage(config: SqlStorageConfig | null): string[] {
  const problems = sqlStorageProblems(config)
  return [
    ...problems.general.map((message) => `SQL server storage: ${message}`),
    ...(['data', 'log', 'tempDb'] as StorageSection[]).flatMap((section) =>
      [problems[section].location, problems[section].count].filter((message): message is string => message !== null).map((message) => `${MESSAGE_PREFIX[section]}: ${message}`),
    ),
  ]
}
